import { Image as UnpicImage } from "@unpic/react"
import type { ImgHTMLAttributes, ReactElement } from "react"

const UnpicOptimizedImage = UnpicImage as unknown as (
  props: OptimizedImageProps & { fetchpriority?: "auto" | "high" | "low" }
) => ReactElement

export type OptimizedImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "alt" | "decoding" | "fetchPriority" | "height" | "loading" | "src" | "srcSet" | "width"
> & {
  alt?: string
  decoding?: "async" | "sync" | "auto"
  fetchPriority?: "auto" | "high" | "low"
  height?: number
  loading?: "lazy" | "eager"
  priority?: boolean
  src?: string
  srcSet?: string
  width?: number
}

export function OptimizedImage({
  alt = "",
  decoding,
  fetchPriority,
  height,
  loading,
  priority = false,
  src,
  srcSet,
  width,
  ...props
}: OptimizedImageProps) {
  const resolvedFetchPriority = priority ? "high" : fetchPriority
  const resolvedLoading = loading ?? (priority || resolvedFetchPriority === "high" ? "eager" : undefined)
  const nativeProps = {
    ...props,
    alt,
    decoding,
    fetchpriority: resolvedFetchPriority,
    height,
    loading: resolvedLoading,
    src,
    srcSet,
    width,
  }

  if (srcSet || !src || src.endsWith(".svg")) {
    return <img {...nativeProps} />
  }

  return (
    <UnpicOptimizedImage
      {...props}
      alt={alt}
      decoding={decoding}
      fetchpriority={resolvedFetchPriority}
      height={height}
      loading={resolvedLoading}
      priority={priority || resolvedFetchPriority === "high"}
      src={src}
      width={width}
    />
  )
}

export default OptimizedImage
